import React from "react";
import { Formik } from "formik";
import {
    TextField,
    withStyles,
    Button,
    Typography,
    MenuItem,
    FormControl,
    FormGroup,
    InputLabel,
    FormHelperText,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Select
} from "@material-ui/core";
import * as Yup from "yup";
import { isSuperUser } from "../../sc-utilities";
import { ThemeProvider } from '@material-ui/styles';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { createMuiTheme } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { billingModes, accountStatuses, languages, termsOfPayment } from '../../sc-utilities/accountUtilities'
import {getDateFormat} from "../../sc-utilities/date";

const styles = theme => ({
    inputRoot: {
        backgroundColor: 'blue',
        color: "purple",
        "& .MuiOutlinedInput-notchedOutline": {
            backgroundColor: "green"
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            backgroundColor: "red"
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            backgroundColor: "purple"
        }
    }, groupUl: { backgroundColor: 'pink' },

    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    listbox: { backgroundColor: 'white' },
    submit: {
        width: "120px",
        marginTop: "10px",
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    cancel: {
        width: "120px",
        marginTop: "10px",
        marginRight: "10px",
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    formControl: {
        width: "100%"
    },
    datePicker: {
        width: '100%'
    },
    buttons: {
        textAlign: "right"
    },
});

const defaultMaterialThemeAdmin = createMuiTheme({
    palette: {
        primary: { 500: '#0C5A99' }
    }
});

const defaultMaterialTheme = createMuiTheme({
    palette: {
        primary: { 500: '#140D8A' }
    }
});

const AccountForm = props => {
    const {
        handleSubmit,
        initialValues,
        formSettings,
        classes, users, loading, handleBack, translations, disabled, handleNext, formHelperTextBottom, editForm, backValid, data, countryList, usStatesList } = props;

    let submitAction = ''
    const key = 'account';

    return (
        <div className={classes.root}>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={async (values, { setSubmitting }) => {
                    if (submitAction === 'finish') {
                        setSubmitting(true);
                        await handleSubmit(values);
                        setSubmitting(false);
                    }

                    if (submitAction === 'next') {
                        handleNext(values, key)
                    }
                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue, setValues, setTouched } = formikProps;

                    const handleCheckDelivery = (e) => {
                        let addressValue = e.target.checked
                        // If checked, populate customer address info
                        if (addressValue) {
                            updateAddressValues(values.CustomerID)
                        } else if (!addressValue) {
                            emptyFields();
                        }
                    }

                    const updateAddressValues = (customerId) => {
                        const customer = data.customer;
                        if (customer) {
                            setTouched({
                                ...touched,
                                "AddressLine1": true,
                                "AddressLine2": true,
                                "State": true,
                                "Country": true,
                                "City": true
                            })
                            setValues({
                                ...values,
                                "AddressLine1": customer?.AddressLine1 || "",
                                "AddressLine2": customer?.AddressLine2 || "",
                                "State": customer?.State || "",
                                "Country": customer?.Country || "",
                                "City": customer?.City || "",
                                "CustomerAddress": true,
                            })
                        }
                    }

                    const emptyFields = () => {
                        setValues({
                            ...values,
                            "AddressLine1": "",
                            "AddressLine2": "",
                            "State": "",
                            "Country": "",
                            "City": "",
                            "CustomerAddress": false,
                        })
                    }

                    const inputProps = {
                        autoComplete: 'new-password',
                        form: {
                          autoComplete: 'off',
                        },
                      }

                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={'h6'}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>
                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>{translations['billing_mode'] || 'Billing mode'}</InputLabel>
                                <Select disabled={editForm} name="BillingMode" margin={'dense'} value={values.BillingMode} onChange={handleChange}>
                                    {(billingModes(translations)).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>{translations['billing_account_status'] || 'Billing account status'}</InputLabel>
                                <Select name="Status" margin={'dense'} value={values.Status} onChange={handleChange}>
                                    {(accountStatuses(translations)).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <ThemeProvider theme={isSuperUser() ? defaultMaterialThemeAdmin : defaultMaterialTheme}>
                                <FormHelperText>{translations['activation_date'] || 'Activation date'}</FormHelperText>
                                <KeyboardDatePicker
                                    name={"ActivationDate"}
                                    disabled={editForm}
                                    className={classes.datePicker}
                                    disablePast
                                    clearable={true}
                                    onChange={date => setFieldValue('ActivationDate', date)}
                                    format={getDateFormat()}
                                    value={values.ActivationDate}
                                    margin="dense"
                                    placeholder={translations['activation_date_text'] || 'Please select from which day account will be active (default is now)'}
                                />
                                {formHelperTextBottom ? <FormHelperText>{formHelperTextBottom}</FormHelperText> : null}
                            </ThemeProvider>
                            <ThemeProvider theme={isSuperUser() ? defaultMaterialThemeAdmin : defaultMaterialTheme}>
                                <FormHelperText>{translations['expires_at'] || 'Expires at'}</FormHelperText>
                                <KeyboardDatePicker
                                    name={"ExpiresAt"}
                                    disabled={editForm}
                                    className={classes.datePicker}
                                    disablePast
                                    clearable={true}
                                    onChange={date => setFieldValue('ExpiresAt', date)}
                                    format={getDateFormat()}
                                    value={values.ExpiresAt}
                                    minDate={values?.ActivationDate}
                                    margin="dense"
                                    placeholder={translations['expires_at_text'] || 'Please select expiration date'}
                                />
                                {formHelperTextBottom ? <FormHelperText>{formHelperTextBottom}</FormHelperText> : null}
                            </ThemeProvider>

                            <TextField
                                disabled={editForm}
                                name="BillingDay"
                                value={values.BillingDay}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                InputProps={{
                                    inputProps: {
                                        max: 28, min: 1
                                    },
                                    autoComplete: 'new-password',
                                    form: {
                                        autoComplete: 'off',
                                    },
                                }}
                                error={!!(errors.BillingDay && touched.BillingDay)}
                                placeholder={translations['billing_day_text'] || 'Please select on which day account will be billed'}
                                label={translations['billing_day'] || 'Billing day'}
                                margin={'dense'}
                                fullWidth={true}
                                type="number"
                            />

                            <TextField
                                name="CustomerOrder"
                                value={values.CustomerOrder}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.CustomerOrder && touched.CustomerOrder)}
                                placeholder={translations['customer_order'] || 'Customer order'}
                                label={translations['customer_order'] || 'Customer order'}
                                margin={'dense'}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />

                            <TextField
                                name="VAT"
                                value={values.VAT}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.VAT && touched.VAT)}
                                placeholder={translations['vat_number'] || 'VAT Number'}
                                label={translations['vat_number'] || 'VAT Number'}
                                margin={'dense'}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />

                            <TextField
                                name="TaxClassification"
                                value={values.TaxClassification}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.TaxClassification && touched.TaxClassification)}
                                placeholder={translations['tax_classification'] || 'Tax Classification'}
                                label={translations['tax_classification'] || 'Tax Classification'}
                                margin={'dense'}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />

                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>{translations['terms_of_payment_text'] || 'Please select terms of payment'}</InputLabel>
                                <Select name="TermsOfPayment" margin={'dense'} value={values.TermsOfPayment} onChange={handleChange}>
                                    {(termsOfPayment ? [...termsOfPayment] : []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <ThemeProvider theme={isSuperUser() ? defaultMaterialThemeAdmin : defaultMaterialTheme}>
                                <FormControl className={classes.formControl} disabled={disabled} margin="dense">
                                    <Autocomplete
                                        classes={{ listbox: classes.listbox, root: classes.listbox, groupLabel: classes.listbox, option: classes.listbox }}
                                        name={'Accountant'}
                                        forcePopupIcon={false}
                                        id="combo-box-demo"
                                        options={users ? users : []}
                                        value={values.Accountant ? values.Accountant : null}
                                        onChange={(event, newValue) => {
                                            setFieldValue('Accountant', newValue);
                                        }}
                                        getOptionLabel={option => (typeof option.displayValue === 'string' ? option.displayValue : '')}
                                        getOptionSelected={(option, value) => option.value === value.value}
                                        renderInput={params => <TextField {...params} label={translations['accountant'] || 'Accountant'} margin="dense" fullWidth={true} />}
                                    />
                                </FormControl>
                            </ThemeProvider>

                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>{translations['please_select_language'] || 'Please select language'}</InputLabel>
                                <Select name="Language" margin={'dense'} value={values.Language} onChange={handleChange}>
                                    {(languages(translations)).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.displayValue}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                name="PhoneNumber"
                                value={values.PhoneNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.PhoneNumber && touched.PhoneNumber)}
                                placeholder={translations['phone_number'] || 'Phone number'}
                                label={translations['phone_number'] || 'Phone number'}
                                margin={'dense'}
                                fullWidth={true}
                                type="tel"
                                inputProps={inputProps}
                            />

                            <TextField
                                name="Email"
                                value={values.Email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Email && touched.Email)}
                                placeholder={translations['e_mail'] || 'E-Mail'}
                                label={translations['e_mail'] || 'E-Mail'}
                                margin={'dense'}
                                fullWidth={true}
                                type="email"
                                inputProps={inputProps}
                            />

                            {!editForm ? (
                                <FormGroup>
                                    <FormControlLabel
                                        control={<Checkbox margin={'dense'} name={'CustomerAddress'} checked={values.CustomerAddress} onChange={(e) => handleCheckDelivery(e)} />}
                                        label={translations['same_as_customer_address'] || 'Delivery address same as customer address? (Checked - Yes)'}
                                    />
                                </FormGroup>
                            ) : null}

                            <Autocomplete
                                name="Country"
                                margin={"dense"}
                                options={countryList ? [...countryList] : []}
                                value={values?.Country || ''}
                                onChange={(_event, newValue) => {
                                    if (newValue?.key !== 'US') {
                                        setValues({...values, 'Country': newValue, 'State': ''});
                                    } else {
                                        setValues({...values, 'Country': newValue, 'State': null});
                                    }
                                }}
                                getOptionLabel={option => option?.value ?? option ?? ''}
                                renderInput={params => <TextField {...params} label={translations['country'] || 'Country'} margin="dense" fullWidth={true} />}
                                disabled={values.CustomerAddress}
                            />

                            { values?.Country && (values?.Country['key'] === 'US' || (values?.Country === 'US')) ?
                                <Autocomplete
                                    name="State"
                                    margin={"dense"}
                                    options={usStatesList ? [...usStatesList] : []}
                                    value={values?.State || ''}
                                    onChange={(_event, newValue) => {
                                        setFieldValue('State', newValue);
                                    }}
                                    getOptionLabel={option => option?.value || ''}
                                    renderInput={params => <TextField {...params} label={translations['state'] || 'State'} margin="dense" fullWidth={true} />}
                                    disabled={values.CustomerAddress}
                                /> : null}

                            <TextField
                                name="City"
                                value={values.City || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.City && touched.City)}
                                placeholder={translations['city'] || 'City'}
                                label={translations['city'] || 'City'}
                                margin={'dense'}
                                disabled={values.CustomerAddress}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />

                            <TextField
                                name="AddressLine1"
                                value={values.AddressLine1 || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.AddressLine1 && touched.AddressLine1)}
                                placeholder={translations["address_line1"] || "Address Line1"}
                                label={translations["address_line1"] || "Address Line1"}
                                margin={'dense'}
                                disabled={values.CustomerAddress}
                                fullWidth={true}
                                type="text"
                                inputProps={inputProps}
                            />

                            <TextField
                                name="AddressLine2"
                                value={values?.AddressLine2 || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.AddressLine2 && touched.AddressLine2)}
                                placeholder={translations["address_line2"] || "Address Line2"}
                                label={translations["address_line2"] || "Address Line2"}
                                margin={"dense"}
                                disabled={values.CustomerAddress}
                                fullWidth={true}
                                inputProps={inputProps}
                            />

                            <div className={classes.buttons} >
                                {formSettings.ButtonCancel ? (

                                    <Button
                                        className={classes.cancel}
                                        variant="contained"
                                        color={formSettings.ButtonCancel.Color}
                                        onClick={formSettings.ButtonCancel.handleCancel}
                                        disabled={loading}
                                    >
                                        {formSettings.ButtonCancel.Title}
                                    </Button>
                                ) : null
                                }

                                {formSettings.ButtonBack ? (

                                    <Button
                                        className={classes.cancel}
                                        variant="contained"
                                        color={formSettings.ButtonBack.Color}
                                        onClick={() => handleBack(values, key)}
                                        disabled={loading}
                                    >
                                        {formSettings.ButtonBack.Title}
                                    </Button>
                                ) : null
                                }          
                                {/* {formSettings.ButtonSkip ? (

                                    <Button
                                        className={classes.cancel}
                                        variant="contained"
                                        color={formSettings.ButtonSkip.Color}
                                        onClick={formSettings.ButtonSkip.handleSkip}
                                        disabled={loading}
                                    >
                                        {formSettings.ButtonSkip.Title}
                                    </Button>
                                ) : null
                                } */}
                                {/* {formSettings.ButtonNext ? (
                                    <Button
                                        className={classes.cancel}
                                        variant="contained"
                                        color={formSettings.ButtonNext.Color}
                                        onClick={() => {
                                            submitAction = 'next';
                                            formikProps.submitForm();
                                        }}
                                        type="submit"
                                        disabled={backValid[key] ? !backValid[key] : loading || !(isValid && dirty) || isSubmitting}
                                    >
                                        {formSettings.ButtonNext.Title}
                                    </Button>
                                ) : null
                                } */}
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        onClick={() => {
                                            submitAction = 'finish';
                                            formikProps.submitForm();
                                        }}
                                        disabled={backValid[key] ? !backValid[key] : loading || !(isValid) || isSubmitting}
                                    >
                                        {loading || isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default withStyles(styles, { withTheme: true })(AccountForm);
